import { h } from 'vue'
import icon from './icons'
import { SIGNER_ROLE_NAMES, ROLE_DEFAULTS } from './signer_roles'

// Brand chrome for TemplateCreator + EnvelopeCreator.
// Values are CSS var() references (defined on :root in application.scss) so the whole app
// shares one palette. Fallbacks match the create-modal purple if vars are missing.
export const C = {
  primary: 'var(--brand, #1E3A6E)',
  primaryDark: 'var(--brand-dark, #152C54)',
  primarySoft: 'var(--brand-soft, #EEF1F6)',
  primaryMid: 'var(--brand-mid, #DDE3ED)',
  primaryBorder: 'var(--brand-border, #C7D2E4)',
  text: 'var(--brand-text, #152C54)',
  muted: '#6B7280',
  border: '#E5E7EB',
  softBorder: '#F3F4F6',
  danger: '#EF4444',
  white: '#FFFFFF'
}

export const s = {
  overlay: 'position:fixed;inset:0;background:rgba(15,23,42,.45);z-index:9999;display:flex;align-items:center;justify-content:center;padding:20px;box-sizing:border-box',
  modal: `background:${C.white};width:100%;max-width:920px;max-height:calc(100vh - 40px);display:flex;flex-direction:column;overflow:hidden;border-radius:16px;box-shadow:0 25px 50px -12px rgba(0,0,0,.28)`,
  header: `background:${C.white};border-bottom:1px solid ${C.border};padding:16px 28px;display:flex;align-items:center;justify-content:space-between;gap:16px;flex-shrink:0`,
  body: 'flex:1;overflow:auto;padding:4px 28px 8px;background:#fff',
  footer: `border-top:1px solid ${C.border};padding:14px 28px;display:flex;justify-content:flex-end;align-items:center;gap:10px;flex-shrink:0;background:${C.white}`,
  label: `display:block;font-size:12px;font-weight:600;color:${C.text};margin-bottom:6px`,
  input: `width:100%;box-sizing:border-box;padding:10px 12px;border:1px solid ${C.border};border-radius:8px;font-size:13px;font-family:inherit;outline:none;background:${C.white};color:${C.text};transition:border-color .15s`,
  btn: `display:inline-flex;align-items:center;gap:6px;padding:10px 20px;border:none;border-radius:8px;font-weight:600;font-size:13px;cursor:pointer;font-family:inherit;background:${C.primary};color:#fff`,
  btnSec: `display:inline-flex;align-items:center;gap:6px;padding:10px 18px;border:1px solid ${C.border};border-radius:8px;font-weight:600;font-size:13px;cursor:pointer;font-family:inherit;background:${C.white};color:${C.text}`,
  btnGhost: `display:inline-flex;align-items:center;gap:6px;padding:7px 14px;border:1px solid ${C.border};border-radius:8px;font-weight:600;font-size:12px;cursor:pointer;font-family:inherit;background:${C.white};color:${C.text}`,
  dropzone: `border:1.5px dashed ${C.primaryBorder};border-radius:12px;background:${C.primarySoft};padding:36px 24px;text-align:center`,
  tableWrap: `border:1px solid ${C.border};border-radius:10px;overflow:hidden;background:${C.white}`,
  tableHead: `display:grid;gap:8px;padding:8px 12px;background:#FAFAFC;border-bottom:1px solid ${C.border};font-size:10px;font-weight:700;color:${C.muted};letter-spacing:.4px;text-transform:uppercase`,
  tableRow: `display:grid;gap:8px;padding:10px 12px;border-bottom:1px solid ${C.softBorder};align-items:center;background:${C.white}`,
  addLink: `display:inline-flex;align-items:center;gap:6px;margin-top:12px;padding:8px 14px;border:1px solid ${C.primaryBorder};border-radius:8px;background:${C.primarySoft};color:${C.primary};cursor:pointer;font-size:13px;font-weight:600;font-family:inherit`
}

export function modalHeader ({ iconName, title, subtitle, onAdvanced, onClose }) {
  return h('div', { style: s.header }, [
    h('div', { style: 'display:flex;align-items:center;gap:12px;min-width:0' }, [
      h('div', {
        style: `width:40px;height:40px;border-radius:10px;background:${C.primarySoft};color:${C.primary};display:flex;align-items:center;justify-content:center;flex-shrink:0`
      }, icon(iconName, 20)),
      h('div', { style: 'min-width:0' }, [
        h('div', { style: `font-size:16px;font-weight:700;color:${C.text};line-height:1.2` }, title),
        subtitle && h('div', { style: `font-size:12px;color:${C.muted};margin-top:2px` }, subtitle)
      ])
    ]),
    h('div', { style: 'display:flex;align-items:center;gap:8px;flex-shrink:0' }, [
      onAdvanced && h('button', {
        onClick: onAdvanced,
        style: s.btnGhost
      }, [icon('settings', 14), ' Advanced options']),
      h('button', {
        onClick: onClose,
        style: `width:32px;height:32px;border:1px solid ${C.border};border-radius:8px;background:${C.white};cursor:pointer;color:${C.muted};display:flex;align-items:center;justify-content:center`
      }, icon('x', 14))
    ])
  ])
}

/** Two-column section: left rail (icon + title + help), right content. */
export function section ({ icon: iconName, title, subtitle }, children) {
  const content = Array.isArray(children) ? children : (children ? [children] : [])
  return h('div', {
    style: `display:grid;grid-template-columns:minmax(160px,200px) 1fr;gap:20px 28px;padding:24px 0;border-bottom:1px solid ${C.border}`
  }, [
    h('div', { style: 'display:flex;gap:10px;align-items:flex-start' }, [
      h('div', {
        style: `width:34px;height:34px;border-radius:50%;background:${C.primarySoft};color:${C.primary};display:flex;align-items:center;justify-content:center;flex-shrink:0`
      }, icon(iconName, 16)),
      h('div', null, [
        h('div', { style: `font-size:13px;font-weight:700;color:${C.text};margin-bottom:3px` }, title),
        subtitle && h('div', { style: `font-size:12px;color:${C.muted};line-height:1.45` }, subtitle)
      ])
    ]),
    h('div', { style: 'min-width:0;display:flex;flex-direction:column;gap:14px' }, content)
  ])
}

export function fieldLabel (text, required = false) {
  return h('label', { style: s.label }, [
    text,
    required ? h('span', { style: `color:${C.danger};margin-left:2px` }, ' *') : null
  ])
}

export function dropzoneShell (children, { onDragover, onDrop } = {}) {
  return h('div', {
    style: s.dropzone,
    onDragover: onDragover || (e => e.preventDefault()),
    onDrop
  }, children)
}

export function uploadSplitButton ({ onUpload, onToggle, open, menuItems }) {
  return h('div', { style: 'position:relative;display:inline-flex', onClick: e => e.stopPropagation() }, [
    h('button', {
      onClick: onUpload,
      style: `padding:10px 18px;background:${C.primary};color:#fff;border:none;border-radius:8px 0 0 8px;font-size:13px;font-weight:600;cursor:pointer;font-family:inherit`
    }, 'Upload'),
    h('button', {
      onClick: onToggle,
      style: `padding:10px 10px;background:${C.primaryDark};color:#fff;border:none;border-left:1px solid rgba(255,255,255,.25);border-radius:0 8px 8px 0;font-size:11px;cursor:pointer;display:flex;align-items:center`
    }, icon('chevron_down', 14)),
    open && h('div', {
      style: `position:absolute;top:100%;left:0;margin-top:6px;background:${C.white};border:1px solid ${C.border};border-radius:10px;box-shadow:0 10px 30px rgba(0,0,0,.12);min-width:200px;z-index:100;overflow:hidden`,
      onClick: e => e.stopPropagation()
    }, menuItems)
  ])
}

export function menuItem (label, iconName, onClick) {
  return h('button', {
    onClick,
    style: `width:100%;padding:11px 14px;border:none;background:${C.white};text-align:left;cursor:pointer;font-size:13px;color:${C.text};font-family:inherit;display:flex;align-items:center;gap:10px`,
    onMouseenter: e => { e.currentTarget.style.background = 'var(--brand-soft, #EEF1F6)' },
    onMouseleave: e => { e.currentTarget.style.background = '#FFFFFF' }
  }, [icon(iconName, 16), label])
}

export function accent (color = C.primary) {
  return color
}

// One select, two options, wherever a signing role is chosen — both creator modals and both of
// their recipient tables. Anything already stored that is not one of the two (an older
// envelope, a merged template's own role) stays selectable rather than being silently
// rewritten to ApTask Onboarding.
export function signerRoleSelect (roleName, onChange) {
  const options = !roleName || SIGNER_ROLE_NAMES.includes(roleName)
    ? SIGNER_ROLE_NAMES
    : [...SIGNER_ROLE_NAMES, roleName]

  return h('select', {
    value: roleName || SIGNER_ROLE_NAMES[0],
    onChange: (e) => onChange(e.target.value),
    style: s.input + ';cursor:pointer'
  }, options.map((name) => h('option', { value: name }, name)))
}

// Delete-signer control at the right end of a recipient row. Always rendered, on every row
// including the only one: a control that appears once a second recipient exists reads as
// missing, and it is the row's own affordance, not a property of how many rows there are.
// Callers are responsible for what "delete the last row" means (see removeRecipient).
export function deleteRecipientButton (onClick, title = 'Delete signer') {
  return h('button', {
    onClick,
    title,
    style: 'border:none;background:none;cursor:pointer;color:#9ca3af;display:flex;padding:4px;' +
      'border-radius:6px;transition:color .12s,background .12s',
    onMouseenter: (e) => { e.currentTarget.style.color = '#dc2626'; e.currentTarget.style.background = '#fef2f2' },
    onMouseleave: (e) => { e.currentTarget.style.color = '#9ca3af'; e.currentTarget.style.background = 'none' }
  }, icon('trash', 15))
}

// Switching to a role that has a usual person fills the blanks, and only the blanks — a name
// or address already typed is never overwritten.
export function applyRoleDefaults (recipient, roleName) {
  const defaults = ROLE_DEFAULTS[roleName]

  if (!defaults) return

  if (!(recipient.name || '').trim()) recipient.name = defaults.default_name
  if (!(recipient.email || '').trim()) recipient.email = defaults.email
}

// The "Add message" step's saved-wording controls: a picker that fills the subject/body inputs
// beside it, and a Save-as button that files whatever is currently typed there for reuse.
//
// Takes refs rather than values because both creator modals build their whole tree inside one
// render function -- reading .value here is what makes the picker re-render when its list
// arrives from the server, and writing messageRef.value is what makes the inputs update.
//
// The picker resets itself to the placeholder after every pick on purpose: it is an action, not
// a stored choice. Nothing records which saved message a given envelope was built from, so
// leaving it selected would assert a link that does not exist, and re-picking the same entry
// (after editing the text and wanting the original back) would fire no change event at all.
export function messageTemplateControls ({ templatesRef, messageRef, saveStateRef, onSave }) {
  const state = saveStateRef.value

  return h('div', { style: 'display:flex;flex-direction:column;gap:10px' }, [
    h('div', { style: 'display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap' }, [
      h('div', { style: 'flex:1;min-width:220px' }, [
        fieldLabel('Use a saved message'),
        h('select', {
          value: '',
          disabled: templatesRef.value.length === 0,
          onChange: e => {
            const picked = templatesRef.value.find(t => String(t.id) === e.target.value)

            if (picked) {
              messageRef.value.subject = picked.subject || ''
              messageRef.value.body = picked.body || ''
            }

            e.target.value = ''
          },
          style: s.input + ';cursor:pointer'
        }, [
          h('option', { value: '' }, templatesRef.value.length
            ? 'Select a saved message…'
            : 'No saved messages yet — save one below'),
          ...templatesRef.value.map(t => h('option', { key: t.id, value: String(t.id) }, t.name))
        ])
      ]),
      h('button', {
        type: 'button',
        onClick: () => {
          state.error = ''
          state.open = !state.open
        },
        style: s.btnGhost + ';height:40px;flex-shrink:0'
      }, state.open ? 'Cancel' : 'Save as template')
    ]),
    state.open
      ? h('div', { style: `display:flex;gap:8px;align-items:flex-end;flex-wrap:wrap;padding:12px;border:1px solid ${C.border};border-radius:10px;background:#FAFAFC` }, [
        h('div', { style: 'flex:1;min-width:220px' }, [
          fieldLabel('Name this message'),
          h('input', {
            value: state.name,
            placeholder: 'e.g. Offer letter',
            onInput: e => { state.name = e.target.value },
            style: s.input
          })
        ]),
        h('button', {
          type: 'button',
          disabled: state.saving || !state.name.trim(),
          onClick: onSave,
          style: s.btn + `;height:40px;flex-shrink:0;${state.saving || !state.name.trim() ? 'opacity:.5;cursor:default' : ''}`
        }, state.saving ? 'Saving…' : 'Save')
      ])
      : null,
    state.error
      ? h('div', { style: `font-size:12px;color:${C.danger}` }, state.error)
      : null
  ])
}
